import classnames from 'classnames';
import React, { useCallback, useState, useRef, useMemo, memo, useEffect } from 'react';
import { clamp, deepFilter, deepMap, isNil } from '@/utils';
import {
  OfficialAssetCategory,
  OfficialAssetContent,
  UserAssetContent,
  UserAssetFolderList,
} from './components';
import { UserAssetCategoryItemType } from './components/UserAssetList/components';
import useWindowSize from './UserWindowSize.';
import { AssetItem, AssetType } from '@/type/assetListItem';
import { findItemInTreeList } from '@/utils/treeList';
import { useGetUserAssetList } from '@/hooks/AssetList/user';
import { OfficialResourceCategory, OfficialResourceCategoryItem } from '@/type/assetList';
import { ROOT_CATEGORY_ID } from '@/constants/assetList';
import { useGetOfficialResources, useOfficialResourceCategoryApi } from '@/hooks/server';
import { OfficialResourceAPI } from '@/server/AssetManage';

export const ROOT_FOLDER_ID = '-1'; // 约定“-1”为'全部'，即根文件夹
export const ROOT_USER_FOLDER_NAME = '我的'; // 即根文件夹
export const ROOT_OFFICIAL_FOLDER_ID = '0';
export const ROOT_OFFICIAL_FOLDER_NAME = '全部资源';

export interface AssetListPopupProps<T extends boolean> {
  height: number;
  allowMulti?: T;
  className?: string;
  selectedType: AssetType;
  onSelect: T extends true ? (item: AssetItem[]) => void : (item: AssetItem) => void;
}

export type SelectedCategoryType = {
  id: string;
  name: string;
};

export function AssetListPopupForQuickSelect<T extends boolean = false>({
  className,
  height,
  selectedType,
  allowMulti,
  onSelect,
}: AssetListPopupProps<T>) {
  // 资源库本身状态、回调等数据
  const cateTreeListRef = useRef<HTMLDivElement | null>(null);
  //  0->官方  /  1->我的
  const [type, setType] = useState<'0' | '1'>('1');

  const { width: windowWidth } = useWindowSize();
  const [cateListWidth, setCateListWidth] = useState(180);

  // 官方资源库状态、回调等数据
  const { data: officialAssetList } = useGetOfficialResources();
  const { officialCategoriesTreeList: _officialCategoriesTreeList, officialCategoriesMap } =
    useOfficialResourceCategoryApi();

  const officialCategoriesTreeList: OfficialResourceCategory[] | undefined = useMemo(() => {
    if (!_officialCategoriesTreeList) return undefined;
    const data: OfficialResourceCategory[] = deepMap<
      OfficialResourceCategoryItem,
      OfficialResourceCategory
    >(
      _officialCategoriesTreeList,
      (data) => ({
        ...data,
        id: data.id.toString(),
        parentId: data.parentId?.toString(),
        children: [],
      }),
      'children',
    );

    return data;
  }, [_officialCategoriesTreeList]);

  const [selectedOfficialCategory, setSelectedOfficialCategory] = useState<SelectedCategoryType>({
    id: ROOT_CATEGORY_ID,
    name: ROOT_OFFICIAL_FOLDER_NAME,
  });

  const filteredOfficialAssetList: AssetItem[] | undefined = useMemo(() => {
    return officialAssetList?.filter((a) => {
      const categoryRule =
        selectedOfficialCategory.id === ROOT_CATEGORY_ID
          ? true
          : a.parentId === selectedOfficialCategory.id ||
            officialCategoriesMap?.get(Number(a.parentId))?.parentId?.toString() ===
              selectedOfficialCategory.id;
      const typeRule = a.type === selectedType || a.type === 'folder';
      return categoryRule && typeRule;
    });
  }, [selectedOfficialCategory.id, officialAssetList]);

  const handleSelectOfficialCategory = useCallback((item: SelectedCategoryType) => {
    setSelectedOfficialCategory(item);
  }, []);

  // 用户资源库状态、回调等数据
  const { data: userAssetList } = useGetUserAssetList({
    pageNum: 1,
    pageSize: 999,
  });

  const [selectedUserFolder, setSelectedUserFolder] = useState<{
    id: string;
    name: string;
  } | null>({ id: ROOT_FOLDER_ID, name: ROOT_USER_FOLDER_NAME });

  // console.log('UserAssetList: userAssetList', userAssetList);
  const userAssetTreeList = useMemo(() => {
    if (isNil(userAssetList)) {
      return null;
    }

    const rootFolder: AssetItem<'folder'> = {
      type: 'folder',
      name: ROOT_USER_FOLDER_NAME,
      url: '',
      id: ROOT_FOLDER_ID,
      thumb: '',
      children: [],
      content: {},
      parentId: null,
      createdAt: '',
    };

    const loopAddChildren = (root: AssetItem, flatList: AssetItem[]) => {
      root.children = flatList.filter((a) => a.parentId === root.id);
      for (const c of root.children) {
        loopAddChildren(c, flatList);
      }
      return root;
    };

    const modelAndFolderList = userAssetList.filter(
      (a) => a.type === 'folder' || a.type === selectedType,
    );
    const root = loopAddChildren(rootFolder, modelAndFolderList);
    return root;
  }, [selectedType, userAssetList]);

  // console.log('UserAssetList: userAssetTreeList', userAssetTreeList);

  // 含 '全部' 的文件夹目录
  const userFolderTreeList: UserAssetCategoryItemType = useMemo(() => {
    if (isNil(userAssetTreeList)) {
      return {
        id: ROOT_FOLDER_ID,
        name: ROOT_USER_FOLDER_NAME,
        children: [],
        type: 'folder',
        url: '',
        thumb: '',
        parentId: null,
        content: {},
        createdAt: '',
        contentCount: 0,
      };
    }

    const folderAssets = deepFilter(
      userAssetTreeList.children,
      (a) => {
        (a as UserAssetCategoryItemType).contentCount = a.children.filter(
          (a) => a.type !== 'folder',
        ).length;
        return a.type === 'folder';
      },
      'children',
    ) as UserAssetCategoryItemType[];

    const n: UserAssetCategoryItemType = {
      id: ROOT_FOLDER_ID,
      name: ROOT_USER_FOLDER_NAME,
      children: folderAssets,
      type: 'folder',
      url: '',
      thumb: '',
      parentId: null,
      content: {},
      createdAt: '',
      contentCount: userAssetTreeList.children.filter((a) => a.type !== 'folder').length,
    };

    return n;
  }, [userAssetTreeList]);

  const userAssetContent = useMemo(() => {
    if (isNil(userAssetTreeList)) {
      return [];
    }
    const item = findItemInTreeList(userAssetTreeList, (a) => a.id === selectedUserFolder?.id);
    if (isNil(item)) {
      return [];
    }
    return item.children;
  }, [selectedUserFolder, userAssetTreeList]);

  const handleMouseXDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (isNil(cateTreeListRef.current)) return;

      const startX = e.clientX;
      const initialWidth = cateTreeListRef.current.clientWidth;
      const handleMouseMove = (e: MouseEvent) => {
        const newWidth = initialWidth + (e.clientX - startX);
        const a = clamp(newWidth, 180, windowWidth * 0.7);
        setCateListWidth(a);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [windowWidth],
  );

  return (
    <div style={{ height: `${height}px`, width: `100%` }}>
      <div
        className={classnames(
          'w-full transition-all duration-300 z-10 h-full flex flex-col',
          className,
        )}
      >
        {/* Content */}
        <div className='h-0 w-full flex-auto' onContextMenu={(e) => e.preventDefault()}>
          <div className='flex h-full relative'>
            {/* CategoryList */}
            <div
              ref={cateTreeListRef}
              className='absolute h-full flex flex-col bg-bgColor17171A'
              style={{ width: `${cateListWidth}px` }}
            >
              <div className='w-full h-8 flex items-center'>
                <div
                  className={classnames(
                    'w-1/2 border-b leading-7 text-center hover:text-primary cursor-pointer',
                    type === '0' ? 'border-primary text-white' : 'border-FFFFFF14',
                  )}
                  onClick={() => {
                    setType('0');
                  }}
                >
                  官方
                </div>
                <div
                  className={classnames(
                    'w-1/2 border-b leading-7 text-center hover:text-primary cursor-pointer',
                    type === '1' ? ' border-primary text-white' : 'border-FFFFFF14',
                  )}
                  onClick={() => {
                    setType('1');
                  }}
                >
                  我的
                </div>
              </div>

              <div className='w-full h-0 flex-auto overflow-y-auto no-scroll-container py-2'>
                {type === '0' ? (
                  <OfficialAssetCategory
                    className='w-full overflow-y-auto no-scroll-container select-none'
                    data={[
                      {
                        id: ROOT_CATEGORY_ID,
                        name: ROOT_OFFICIAL_FOLDER_NAME,
                        children: [],
                      },
                      ...(officialCategoriesTreeList || []),
                    ]}
                    depth={1}
                    selectedCate={selectedOfficialCategory}
                    onSelectFolder={handleSelectOfficialCategory}
                  />
                ) : (
                  <UserAssetFolderList
                    className='w-full overflow-y-auto no-scroll-container select-none'
                    folderTreeList={userFolderTreeList}
                    selectedFolder={selectedUserFolder}
                    onSelectFolder={setSelectedUserFolder}
                  />
                )}
              </div>
            </div>
            {/* Dragline */}
            <div
              className='absolute w-[1px] bg-FFFFFF0A h-full'
              style={{ cursor: 'ew-resize', left: `${cateListWidth}px` }}
              onMouseDown={handleMouseXDown}
            />
            {/* AssetContent */}
            <div
              className='absolute h-full right-0 bg-bgColor141417 flex flex-col select-none'
              style={{ left: `${cateListWidth + 2}px` }}
            >
              {type === '0' ? (
                <OfficialAssetContent
                  workingFolder={selectedOfficialCategory}
                  data={filteredOfficialAssetList || []}
                  onModelClick={async (item) => {
                    if (allowMulti) {
                      onSelect([item] as any);
                      await OfficialResourceAPI.addUsedTimes(Number(item.id));
                    } else {
                      onSelect(item as any);
                      await OfficialResourceAPI.addUsedTimes(Number(item.id));
                    }
                  }}
                />
              ) : (
                <UserAssetContent
                  workingFolder={selectedUserFolder}
                  data={userAssetContent}
                  onFolderSelect={setSelectedUserFolder}
                  onSelected={(item) => {
                    if (allowMulti) {
                      onSelect([item] as any);
                    } else {
                      onSelect(item as any);
                    }
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
